import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Modal, Message } from '@bedrock/components'
import { useMemoizedFn } from 'ahooks'
import './index.less'
import useHistoryModal, { renderFileList, renderPrice } from "./index";
import { MPCHistoryTransform } from "./util";
import AmountOrder from '#components/AmountOrder';
import {
  biddingHisSupplierBiddingDetail,
  demandDetailHisRoundForSupplier,
  outsourceBiddingGetDetailHisBiddingTurns,
  outsourceDemandDetailHisRoundForSupplier
} from "#services/api";
import { $sType } from '#utils';

const historyAPIMap = {
  inner: {
    common: demandDetailHisRoundForSupplier,
    bidding: biddingHisSupplierBiddingDetail,
  },
  outer: {
    common: outsourceDemandDetailHisRoundForSupplier,
    bidding: outsourceBiddingGetDetailHisBiddingTurns,
  }
}

export function useBidingHistoryModal() {
  const { openHistory } = useHistoryModal()

  const opening = useRef(false)

  const renderList = useMemo(() => {
    return [
      {
        key: 'score',
        title: '供应商评分',
        render: (value: number) => {
          return <span className={'score'}>{value}</span>
        }
      },
      {
        key: 'bidding_opinions',
        title: '比选意见',
      },
      {
        key: 'bidding_opinions_attachment_dtos',
        title: '比选附件',
        render: (fileList: any[]) => {
          return renderFileList({ fileList })
        }
      },
      {
        key: 'bidding_supplier_quotes.0.tender_plan_attachment_dtos',
        title: '比选方案',
        render: (fileList: any[]) => {
          return renderFileList({ fileList })
        }
      },
      {
        key: 'bidding_supplier_quotes.0',
        title: '报价',
        render: (value: {
          quote_price: number,
          currency: string,
        }) => {
          return renderPrice({
            price: value?.quote_price,
            currency: value?.currency,
          })
        }
      },
      {
        key: 'bidding_supplier_quotes.0.quote_price_attachment_dtos',
        title: '报价方案',
        render: (fileList: any[]) => {
          return renderFileList({ fileList })
        }
      }
    ]
  }, [])

  const openBidingHistory = useMemoizedFn(({
    supplierId,
    biddingId,
    supplierName,
  }) => {
    if (opening.current) return Promise.reject('loading')
    const api = historyAPIMap[$sType].bidding;
    const promise = api({
      bidding_id: biddingId,
      supplier_id: supplierId,
    }).then((res) => {
      if (res?.data?.his_dtos?.length) {
        openHistory({
          title: '历史报价轮次' + (supplierName ? '-' + supplierName : ''),
          list: res?.data?.his_dtos || [],
          renderList: renderList,
        })
      } else {
        Message.info('暂无历史报价轮次')
      }
    });
    promise.finally(() => {
      opening.current = false
    })
    return promise
  })

  return openBidingHistory
}

export function useSMHistoryModal(params = {
    isStructured: false,
    detail: {},
}) {
  const { openHistory } = useHistoryModal({
    isStructured: params.isStructured,
    detail: params.detail,
  })

  const opening = useRef(false)

  const renderList = useMemo(() => {
    return [
      {
        key: 'overall_score',
        title: '综合评分',
        render: (value: number) => {
          if (!value) return '--'
          return <span className={'score'}>{value}</span>
        }
      },
      {
        key: 'total_quote_price',
        title: '合计报价',
        render: (value, item) => {
          return (
            <>
              {renderPrice({
                price: item?.total_quote_price,
                currency: item?.total_quote_price_currency,
              })}
              <AmountOrder value={item?.total_quote_price} />
            </>
          )
        }
      },
      {
        key: 'quote_price_attachment_dtos',
        title: '供应商比选方案\n' +
          '及相关附件',
        render: (fileList: any[]) => {
          return renderFileList({ fileList })
        }
      }
    ]
  }, [])

  const openModal = useMemoizedFn(({
    supplierId,
    businessId,
    supplierName,
  }: {
    supplierId: string | number,
    businessId: string | number,
    supplierName: string,
  }) => {
    if (opening.current) return Promise.reject('loading')
    const api = historyAPIMap[$sType].common;
    const promise = api({
      demand_id: +businessId,
      supplier_id: +supplierId,
    }).then((res) => {
      const historyList = res?.data?.demand_suppliers || []
      if (historyList?.length) {
        if (params.isStructured) {
          openHistory({
            title: '历史报价轮次' + (supplierName ? '-' + supplierName : ''),
            list: historyList || [],
            renderList: renderList,
          })
        } else {
          const dataList = MPCHistoryTransform(res?.data as any);
          openHistory({
            title: '历史报价轮次' + (supplierName ? '-' + supplierName : ''),
            list: dataList || [],
            renderList: renderList,
            quoteTableProps: {
              columns: [
                {
                  dataIndex: 'index',
                  title: '序号',
                  align: 'left',
                  width: 60,
                  render: (value: string, item: any, index: number) => {
                    return index + 1
                  }
                },
                {
                  dataIndex: 'superior_resource_ships_name',
                  title: '合作平台',
                  align: 'left',
                  render: (value: string[]) => {
                    return value.join('、') || '--'
                  }
                },
                {
                  dataIndex: 'cooperate_account',
                  title: '合作内容',
                  align: 'left',
                  render: (value: string) => {
                    return value || '--'
                  }
                },
                {
                  dataIndex: 'remark',
                  title: '备注',
                  align: 'left',
                  render: (value: string) => {
                    return value || '--'
                  }
                },
                {
                  dataIndex: 'show_price',
                  title: '报价金额',
                  align: 'left',
                  render: (value: string, item) => {
                    return (
                        <>
                          {renderPrice({
                            price: item?.quote_price,
                            currency: item?.currency,
                          })}
                          <AmountOrder value={item?.quote_price} />
                        </>
                    )
                  }
                },
              ]
            }
          })
        }

      } else {
        Message.info('暂无历史报价轮次')
      }
    });
    promise.finally(() => {
      opening.current = false
    })
    return promise
  })

  return openModal
}




export const HistoryModalClick = (
  {
    type,
    supplierName,
    children,
    businessId,
    supplierId,
    isStructured = false,
    detail = {},
  }: {
    detail?: any,
    type: 'sm' | 'bidding',
    businessId: string | number,
    supplierId: string | number,
    supplierName: string,
    isStructured?: boolean,
    children: React.ReactElement
  }) => {
  const openSM = useSMHistoryModal({
    isStructured: isStructured,
    detail: detail,
  })
  const openBidding = useBidingHistoryModal()

  return React.cloneElement(children, {
    onClick: () => {
      if (type === 'bidding') {
        openBidding({
          supplierId: supplierId,
          biddingId: businessId,
          supplierName: supplierName,
        })
      } else {
        openSM({
          supplierId: supplierId,
          businessId: businessId,
          supplierName: supplierName,
        })
      }
    }
  })
}
